import { UserGeneratedContentUI } from "./UserGeneratedContentUI";
import { StandardProps } from "../componentMap";
import Script from "next/script";
import { recordImpressionByParam } from "@JohnLewisPartnership/experiments-utils";
import { useMonetateId } from "@/hooks/useMonetateId/useMonetateId.ts";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";

const USER_CONTENT_CLIENT_ID = "web-pdp-scaffold-ui";

type PrefetchUGCParams = {
  apiURL: string;
  productId: string;
  apiKey: string;
};

type UGCResponse = {
  assets: unknown[];
};

/**
 * prefetch UGC API/cache.
 */
export const prefetchUserGeneratedContent = async ({
  apiURL,
  productId,
  apiKey,
}: PrefetchUGCParams): Promise<UGCResponse | undefined> => {
  if (!productId || !apiKey) return;

  const requestUrl = `${apiURL}/v1/content?productId=${encodeURIComponent(productId)}&api_key=${encodeURIComponent(apiKey)}`;

  let response;

  try {
    response = await fetch(requestUrl, {
      method: "GET",
      keepalive: true,
    });
  } catch (e) {
    return;
  }

  if (!response.ok) return;

  return response.json();
};

export function UserGeneratedContent({
  product,
  runtimeConfig,
  experiencesRawObject,
  flags,
  configProps,
}: Pick<
  StandardProps,
  "product" | "runtimeConfig" | "experiencesRawObject" | "flags" | "configProps"
>) {
  const configPosition = configProps?.userGeneratedContentPosition;
  const isPositionMatch =
    !configPosition ||
    configPosition === flags.userGeneratedContentPosition.value;
  const showUGC = flags.showUGC.value === true && isPositionMatch;
  const { monetateId } = useMonetateId();
  const [contentProductId, setContentProductId] = useState<string>();

  const apiURL = runtimeConfig.userGeneratedContentApiBaseURL;
  const productURL = product?.pdpURL.url;
  const productId = product?.aliases.pimProductId;
  const apiKey = runtimeConfig.userGeneratedContentApiKey;
  const monetateEnvironment = runtimeConfig.monetateEnvironment;

  const { ref: inViewRef } = useInView({
    threshold: 0,
    triggerOnce: true,
    skip: !showUGC || contentProductId !== productId,
    onChange: (inView) => {
      if (inView) {
        dispatchMonetateQEvent("WPDP | UGC | In view");
      }
    },
  });

  useEffect(() => {
    if (!showUGC) return;
    let cancelled = false;
    prefetchUserGeneratedContent({
      apiURL,
      productId,
      apiKey,
    }).then((response: UGCResponse | undefined) => {
      if (!cancelled && response !== undefined && response.assets.length > 0) {
        setContentProductId(productId);
        recordImpressionByParam(
          monetateEnvironment,
          experiencesRawObject,
          "pdp-scaffold-observeUGC",
          "true",
          monetateId,
        );
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    apiURL,
    productId,
    apiKey,
    showUGC,
    monetateEnvironment,
    experiencesRawObject,
    monetateId,
  ]);

  if (!showUGC) {
    return;
  }

  return (
    <>
      <Script
        src="/user-content-ui/static/js/jl-user-content.js?includeCoreAssets=false"
        type="text/javascript"
        defer
      />
      <UserGeneratedContentUI
        productId={productId}
        productURL={productURL}
        apiKey={apiKey}
        clientId={USER_CONTENT_CLIENT_ID}
        experiencesRawObject={experiencesRawObject}
        isBrandPromise={true}
        applyMobileNegativeMarginTop={configPosition !== "two"}
        onClick={() => dispatchMonetateQEvent("WPDP | Clicks on UGC")}
        inViewRef={inViewRef}
      />
    </>
  );
}
